import { getEmbeddedKey } from './embeddedKey';

/**
 * Extracts subdomain from the current hostname
 * Returns null if not on a vera.inc subdomain (or a `.localhost` dev host)
 *
 * Examples:
 * - "acme.app.vera.inc" -> "acme"
 * - "acme.app.dev.vera.inc" -> "acme"
 * - "app.vera.inc" -> null (no subdomain)
 * - "acme.localhost" -> "acme" (local development — see below)
 * - "localhost" -> null (no subdomain)
 *
 * ## Why `.localhost` resolves too (VRG-813)
 *
 * Without this, the brand-resolution path is DEAD in local development, and it
 * fails in the one direction that matters. `App.tsx` resolves the company as
 * `subdomain -> embed key -> impersonation -> preview -> a hardcoded id`, where
 * that last literal is the seeded company (Louis Vuitton). So on any dev host
 * this returned null for, every unresolved page silently rendered ONE REAL
 * BRAND'S identity — the demo banner read "not issued by Louis Vuitton" on a
 * different brand's certificate. The fallback's own comment already calls that
 * "a brand leak rather than a graceful default".
 *
 * Browsers resolve `*.localhost` to loopback without any hosts-file entry (it is
 * reserved for exactly this by RFC 6761 §6.3), so `acme-demo.localhost:5315`
 * needs no setup and exercises the SAME code path as `acme-demo.app.vera.inc`.
 * That is the point: the alternative — a dev-only override that bypasses
 * subdomain resolution — would leave the real path untested locally, which is
 * how this defect went unnoticed in the first place.
 *
 * ⚠️ This widens `isDemoHost` and `isVeraOwnedSurface` too, and in both cases
 * toward the safe answer. A `-demo` dev host now correctly gets the demo banner
 * (it previously got none, which is the fail-OPEN that `isDemoHost`'s docblock
 * exists to prevent), and a branded dev host is correctly no longer treated as
 * Vera's own surface. Bare `localhost` and `127.0.0.1` still have no subdomain,
 * so they keep their place in `VERA_OWNED_HOSTS` unchanged.
 */
export const extractSubdomain = (hostnameOverride?: string): string | null => {
  // Injectable so the demo-host decision (below) is testable without a DOM.
  // Defaults to the live hostname in every real call site.
  const hostname = hostnameOverride ?? window.location.hostname;

  // Check if this is a vera app domain
  const appDomainPattern = /^([a-z0-9-]+)\.app\.(dev\.)?vera\.inc$/i;
  const match = hostname.match(appDomainPattern);

  if (match && match[1]) {
    return match[1].toLowerCase();
  }

  /*
   * Local development only. `window.location.hostname` never carries the port,
   * so no port handling is needed here — `acme.localhost:5315` arrives as
   * `acme.localhost`. Anchored to a single `.localhost` label so a real host
   * that merely CONTAINS the string cannot match.
   */
  const localhostPattern = /^([a-z0-9-]+)\.localhost$/i;
  const localMatch = hostname.match(localhostPattern);

  if (localMatch && localMatch[1]) {
    return localMatch[1].toLowerCase();
  }

  return null;
};

/**
 * Checks if the current page is loaded on a company subdomain
 */
export const isOnSubdomain = (): boolean => {
  return extractSubdomain() !== null;
};

/**
 * VRG-309 / VRG-313 — is this an acquisition DEMO host?
 *
 * Derived from the HOSTNAME rather than from the API response, deliberately, and
 * this is a safety property rather than a convenience:
 *
 * `GetCompanyStyleSettingsUseCase` loads the company best-effort and SWALLOWS its
 * own failures — a brand losing its logo for one boot is a cosmetic downgrade, so
 * that lookup must never take the theme down. Hanging the demo banner off that
 * response would inherit the same fail-open: an API hiccup would render a demo
 * page carrying a prospect's scraped logo and product photography with NO
 * disclosure on it. That is the one artifact this epic exists to prevent, so it
 * must fail safe, not open.
 *
 * The hostname is a sound signal because it is guaranteed at the database level:
 * `companies_demo_subdomain_rail` (migration 0073) is a CHECK constraint that
 * makes `kind = 'demo'` imply a `-demo` subdomain, so a demo company cannot be
 * served from any other host. This is the same signal nginx keys its
 * `X-Robots-Tag` on (apps/app/nginx.conf) — one guarantee, enforced twice,
 * independently.
 *
 * Note the direction: the constraint is one-directional, so a STANDARD company on
 * a `-demo` subdomain also matches here (`gucci-demo` is a live example). That
 * over-triggers rather than under-triggers, which is the correct way round — the
 * cost is a demo banner on a page that is informally a demo anyway.
 *
 * Examples:
 * - "ysl-demo.app.vera.inc"     -> true
 * - "ysl.app.vera.inc"          -> false
 * - "demo.app.vera.inc"         -> false (reserved subdomain, not "<brand>-demo")
 */
export const isDemoHost = (hostnameOverride?: string): boolean => {
  const subdomain = extractSubdomain(hostnameOverride);
  return subdomain !== null && subdomain.endsWith('-demo');
};

/**
 * The brand portion of a demo subdomain, for banner copy when the API has not
 * supplied a display name. "ysl-demo" -> "ysl". Returns null off a demo host.
 */
export const demoBrandSlug = (hostnameOverride?: string): string | null => {
  const subdomain = extractSubdomain(hostnameOverride);
  if (!subdomain || !subdomain.endsWith('-demo')) return null;
  return subdomain.slice(0, -'-demo'.length) || null;
};

/**
 * The hosts on which this app is VERA'S OWN product rather than a brand's.
 *
 * An ALLOWLIST, not a blocklist, and that is the whole safety property. See
 * `isVeraOwnedSurface`.
 */
const VERA_OWNED_HOSTS = new Set([
  'app.vera.inc',
  'app.dev.vera.inc',
  // Local development. `extractSubdomain` already returns null for these, but
  // they must be named explicitly because the allowlist is closed by default.
  'localhost',
  '127.0.0.1',
]);

/**
 * Is this page Vera's OWN surface, as opposed to a brand's?
 *
 * `apps/app` is WHITE-LABEL. The same bundle serves two very different things:
 * Vera's own consumer app at `app.vera.inc`, and a brand's certificate
 * experience — either on the brand's subdomain (`acme.app.vera.inc`) or
 * embedded directly into the brand's own storefront via the installation
 * snippet. Anything that carries VERA's branding (the purple auth backdrop is
 * the first such thing) must render on the first, never on the second: painting
 * Vera's brand onto a customer's storefront is precisely the wrong outcome.
 *
 * ## Why an allowlist, and why it fails closed
 *
 * The obvious shape is a blocklist — "hide if a brand signal is present". That
 * is fail-OPEN: an unrecognised host (a brand pointing a custom domain at the
 * app, a new deployment target, a staging alias nobody updated this file for)
 * has no brand signal, so it would show Vera purple on what may well be a
 * brand's page. The failure is silent and lands in front of the customer.
 *
 * An allowlist inverts that. An unknown host is treated as NOT Vera's, so the
 * worst case is that Vera's own page misses a decorative background — a
 * cosmetic downgrade on our own surface, which is the cheap direction to be
 * wrong in. This is the same reasoning, and the same direction, as `isDemoHost`
 * above: derive it from the hostname, and over-trigger the safe way.
 *
 * ## Why it does not read the company API
 *
 * Both signals here are SYNCHRONOUS — `window.location.hostname` and a
 * `<script data-embedded-key>` tag that the install snippet puts in the host
 * page's HTML. So the answer is known at the very first render and there is no
 * loading state to flash through.
 *
 * Gating on the resolved company instead (`CompanyContext`,
 * `useGetCompanyBySubdomain`) would introduce exactly the flash that must not
 * happen: those resolve asynchronously, so every embedded brand page would
 * render "no company yet" for a beat first. If that beat showed the Vera
 * backdrop, every brand would get a purple flash on their own storefront on
 * every load. `isDemoHost`'s docblock rejects an API-derived signal for the
 * same class of reason — a best-effort lookup that swallows its failures cannot
 * carry a decision that must fail safe.
 *
 * @param hostnameOverride  Injectable for tests, like `extractSubdomain`.
 * @param embeddedKeyOverride  Injectable for tests. `null` means "no embed".
 */
export const isVeraOwnedSurface = (
  hostnameOverride?: string,
  embeddedKeyOverride?: string | null,
): boolean => {
  const hostname = (hostnameOverride ?? window.location.hostname).toLowerCase();

  // A brand subdomain is a brand's surface, even though it is a vera.inc host.
  if (extractSubdomain(hostname) !== null) return false;

  // Closed by default: anything not named is treated as somebody else's page.
  if (!VERA_OWNED_HOSTS.has(hostname)) return false;

  /*
   * Defence in depth. The snippet is what makes this an embedded brand
   * experience, and it could in principle be installed on any page — including
   * one served from an allowlisted host. If a brand's key is present, this is
   * the brand's surface regardless of where it is being served from.
   *
   * Read lazily so the override path stays DOM-free for tests.
   */
  const embeddedKey =
    embeddedKeyOverride !== undefined ? embeddedKeyOverride : getEmbeddedKey();
  if (embeddedKey) return false;

  return true;
};
